import { Link, useParams } from "react-router-dom";
import { Header, Icon, Segment } from "semantic-ui-react";
import StaffSharePopup from "../../../../components/StaffSharePopup";
import AppSettings from "../../../../../../config";

interface NotifyContentListCardProps {
  data: {
    id: number;
    authorId?: number;
    body: string;
    cityId: number;
    createdAt: string;
    deactivatedAt?: string | null;
    expiredAt?: string | null;
    headline: string;
    isDraft?: boolean;
    publishedAt: string | null;
    lastNotifiedAt?: string | null;
    scheduledAt?: string | null;
    subscriptionGroups: {
      autoSubscribe: boolean;
      cityId: number;
      id: number;
      name: string;
    }[];
    tagline?: string | null;
    updatedAt: string;
    voiceAudioResourceId?: number | null;
    voiceBody?: string | null;
    voiceForceSendAll?: boolean | null;
  };
  isMobileView?: boolean;
}

export const NotifyContentListCard = (props: NotifyContentListCardProps) => {
  const publishedAtDate = new Date(props.data.publishedAt ?? new Date().toISOString());

  const params = useParams<{ site: string }>();

  // Find the first image URL in the markdown
  const imageRegex = /!\[.*?\]\((.*?)\)/;
  const match = props.data.body.match(imageRegex);
  const firstImageUrl = match ? match[1] : null;

  // Join subscription group names
  const joinedSubscriptionGroups = props.data.subscriptionGroups.map((group) => group.name).join("  •  ");
  const dateAndSubscriptionGroups = `${formattedDate(publishedAtDate)}${
    props.data.subscriptionGroups.length > 0 ? "  •  " + joinedSubscriptionGroups : ""
  }`;

  const strippedMarkdown = stripMarkdown(props.data.body);

  return (
    <Link to={`posts/${props.data.id}`}>
      <Segment className='tw-h-[170px] tw-cursor-pointer tw-p-4'>
        <div className={`tw-flex tw-flex-row ${props.isMobileView ? "tw-h-[85%]" : "tw-h-full"}`}>
          <div
            className={
              firstImageUrl
                ? "tw-flex tw-flex-1 tw-basis-[70%] tw-flex-col"
                : "tw-flex tw-flex-1 tw-basis-full tw-flex-col "
            }
          >
            <Header as='h3' className='tw-mb-0 tw-line-clamp-2'>
              {props.data.headline}
            </Header>

            <div className={`${props.isMobileView ? "tw-mt-4 tw-flex tw-items-center " : "tw-mt-[14px]"}`}>
              <p className='tw-line-clamp-2 tw-overflow-hidden tw-text-ellipsis tw-whitespace-normal tw-text-[#4D4D4D]'>
                {props.data.tagline ?? strippedMarkdown}
              </p>
            </div>
            {/* render the last div here if we are not on a mobile device */}
            {!props.isMobileView && (
              <div className='tw-mt-auto tw-flex tw-text-[#757575]'>
                <p className='tw-mb-0 tw-flex tw-items-center'>{dateAndSubscriptionGroups}</p>
                <StaffSharePopup
                  contentUrl={`${AppSettings.GOGOV_BASE_API_URL}/core/notify/public/share/${params.site}/${props.data.id}`}
                  headline={props.data.headline}
                  position='bottom right'
                  trigger={
                    <Icon
                      name='share'
                      className='tw-ml-auto'
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation(); // Stop event propagation
                        event.preventDefault(); // Prevent default behavior (e.g., navigation)
                      }}
                    />
                  }
                ></StaffSharePopup>
              </div>
            )}
          </div>
          {firstImageUrl && (
            <div className=' tw-ml-1 tw-flex tw-flex-none tw-basis-[30%]  '>
              <img
                src={firstImageUrl}
                alt={`headline ${props.data.headline}`}
                className='tw-h-full tw-w-full tw-object-contain'
              />
            </div>
          )}
        </div>
        {/* Render the last div outside of the columns if we are on a mobile device*/}
        {props.isMobileView && (
          <div
            className={`tw-flex tw-h-[15%] tw-text-[#757575] ${
              props.isMobileView ? "tw-w-full" : firstImageUrl ? "tw-w-[70%]" : "tw-w-full"
            }`}
          >
            <p className='tw-mb-0  tw-flex tw-items-center'>{dateAndSubscriptionGroups}</p>
            <StaffSharePopup
              contentUrl={`${AppSettings.GOGOV_BASE_API_URL}/core/notify/public/share/${params.site}/${props.data.id}`}
              headline={props.data.headline}
              position='bottom right'
              trigger={
                <Icon
                  name='share'
                  className='tw-ml-auto'
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation(); // Stop event propagation
                    event.preventDefault(); // Prevent default behavior (e.g., navigation)
                  }}
                />
              }
            ></StaffSharePopup>
          </div>
        )}
      </Segment>
    </Link>
  );
};

// function that formats Date object into "MMM, DD,YYYY" format
function formattedDate(date: Date) {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}
// function that removes all markdown from a string
function stripMarkdown(markdownText: string): string {
  return markdownText
    .replace(/!\[.*?\]\(.*?\)/g, "") // images
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Extract link text and remove URL
    .replace(/[#]+ .*?/g, "") // headers
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold text
    .replace(/(\*|_)(.*?)\1/g, "$2") // italic text
    .replace(/~~(.*?)~~/g, "$1") // strikethrough text
    .replace(/`{1,2}.*?`{1,2}/g, "") // inline code
    .replace(/```[\s\S]*?```/g, "") // code blocks
    .replace(/>\s?.*/g, "") // blockquotes
    .replace(/[-+*]\s?.*/g, "") // unordered list items
    .replace(/\d+\.\s?.*/g, "") // ordered list items
    .replace(/\\n{2,}/g, " ") // newlines
    .replace(/\\n/g, " ")
    .trim();
}
